import {
  parseAsArrayOf,
  parseAsStringLiteral,
  parseAsString,
  useQueryStates,
} from "nuqs";
import { useMemo } from "react";
import {
  PushAdminUsersFilters,
  PushStatusFilterSchema,
  UserRoleSchema,
} from "@myapp/contract";
import { useDebounce } from "../../../shared/hooks/useDebounce";

/**
 * URL-backed filters for the admin board — shareable and reload-proof, like every list filter.
 *
 * `pushStatus` defaults to 'with': the page exists to send messages, and a list dominated by
 * rows nothing can be sent to buries the two that matter. "Alle" is one select away for the
 * coverage overview. Sorting is deliberately absent — the audience is a handful of names,
 * alphabetical by default, and sort chrome was noise on every header.
 */
export const usePushAdminFilters = () =>
  useQueryStates(
    {
      search: parseAsString.withDefault(""),
      roles: parseAsArrayOf(parseAsStringLiteral(UserRoleSchema.options)).withDefault([]),
      pushStatus: parseAsStringLiteral(PushStatusFilterSchema.options).withDefault("with"),
    },
    { clearOnDefault: true },
  );

export type PushAdminFiltersParams = ReturnType<typeof usePushAdminFilters>[0];

/** Debounced, normalized API shape. Everything the query key needs and nothing transient. */
export function usePushAdminApiFilters(params: PushAdminFiltersParams): PushAdminUsersFilters {
  const search = useDebounce(params.search.trim(), 300);

  return useMemo(
    () => ({
      search: search || undefined,
      roles: params.roles.length > 0 ? params.roles : undefined,
      pushStatus: params.pushStatus,
      limit: 20,
    }),
    [search, params.roles, params.pushStatus],
  );
}
